import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query'
import { useMangoApi } from '~/atoms/MangoApi'

import {
  ApiError,
  MessageSnippet,
  MessageSnippetPostResponse,
  MessageSnippetRequest,
  PaginatedApiResult,
} from '@mangovoice/types'

const SnippetsQueryKeysEnum = {
  SNIPPETS: 'snippets',
}

export const useGetSnippets = (
  pbxId: number | undefined,
  offset: string,
  personal: boolean,
  onQuerySuccess:
    | ((data: PaginatedApiResult<MessageSnippet>) => void)
    | undefined = undefined,
  options: Record<string, unknown> = {},
) => {
  const api = useMangoApi()
  return useQuery({
    queryKey: [SnippetsQueryKeysEnum.SNIPPETS, { pbxId, offset, personal }],
    queryFn: async () => {
      const { data, ok } = await api.getSnippets({ offset, personal, pbxId })
      if (ok) {
        onQuerySuccess?.(data as PaginatedApiResult<MessageSnippet>)
      }
      return data as PaginatedApiResult<MessageSnippet>
    },
    ...options,
    retry: (options.retry as number | boolean | undefined) ?? false,
  })
}

export const useSearchSnippets = (
  search: string,
  onQuerySuccess:
    | ((data: PaginatedApiResult<MessageSnippet>) => void)
    | undefined = undefined,
  options: Omit<
    UseQueryOptions<PaginatedApiResult<MessageSnippet>, ApiError>,
    'queryKey'
  >,
) => {
  const api = useMangoApi()
  return useQuery<PaginatedApiResult<MessageSnippet>, ApiError>({
    queryKey: [SnippetsQueryKeysEnum.SNIPPETS, { search }],
    queryFn: async () => {
      const { ok, data } = await api.searchSnippets(search)
      if (!ok) {
        return Promise.reject(data)
      }
      onQuerySuccess?.(data as PaginatedApiResult<MessageSnippet>)
      return data!
    },
    ...options,
    retry: (options.retry as number | boolean | undefined) ?? false,
  })
}

export const useDeleteSnippet = (
  onSuccess: (data: undefined, snippetId: number) => void,
) => {
  const api = useMangoApi()
  return useMutation({
    mutationFn: async (snippetId) => {
      const { ok, data } = await api.deleteSnippet(snippetId)
      if (!ok) {
        return Promise.reject(data!)
      }
      return data
    },
    onSuccess: onSuccess,
  })
}

export type MessageSnippetUpsertRequest = MessageSnippetRequest & {
  id: number | undefined
}
export const useSaveSnippet = (
  onSuccess: (
    data: MessageSnippetPostResponse,
    snippet: MessageSnippetUpsertRequest,
  ) => void,
  onError: (error: ApiError, snippet: MessageSnippetUpsertRequest) => void,
) => {
  const api = useMangoApi()
  return useMutation({
    mutationFn: async (snippet: MessageSnippetUpsertRequest) => {
      const { ok, data } = await api.postSnippet(snippet)
      if (!ok) {
        return Promise.reject(data!)
      }
      return data!
    },
    onSuccess: onSuccess,
    onError: onError,
  })
}
